<template>
  <div class="material-details" v-if="material">
    <div class="header">
      <div class="material-path">
        <span>Materials: </span>
        <span class="material-path-value">/ {{ isEditMode ? 'Edit Material' : material.name }}</span>
      </div>

      <div class="header-content">
        <h2 class="material-title">{{ isEditMode ? "Edit Material" : material.name }}</h2>
        <div class="top-buttons">
          <button class="icon-btn" @click="toggleEditMode">
            <i class="bi bi-pencil"></i>
          </button>
          <button class="remove-btn" @click="deleteMaterial">
            <i class="bi bi-trash"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Display Mode -->
    <div v-if="!isEditMode">
      <div class="detail"><strong>Name:</strong> <span class="detail-value">{{ material.name }}</span></div>
      <div class="detail"><strong>Batch:</strong> <span class="detail-value">{{ material.batch }}</span></div>

      <!-- Metadata Section -->
        <MetadataSection
          v-if="showMetadata"
          :key="material.node_id"
          :mdListId="material.metadata_list_id"
          :assType="'material'"
          :assId="material.node_id"
          @mdListCreated="updateMdListId"
          @mdListDetached="clearMdListId"
      />
    </div>

    <!-- Edit Mode -->
    <div v-else>
      <div class="form-group">
        <label for="name">Name</label>
        <input id="name" type="text" v-model="editableMaterial.name" />
      </div>

      <div class="form-group">
        <label for="batch">Batch</label>
        <input id="batch" type="text" v-model="editableMaterial.batch" />
      </div>

      <div class="buttons-container">
        <button @click="saveChanges">Save</button>
        <button @click="cancelEditMode">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script>
import { API_ENDPOINTS } from '@/services/config';
import apiService from '@/services/apiService';
import { useToast } from 'vue-toastification';
import NodeMdList from '@/components/NodeMdList.vue';
import { confirmDelete } from "@/utils/confirmDelete";
import { mapState } from 'vuex';

export default {
  name: 'MaterialDetails',
  components: { MetadataSection: NodeMdList },
  props: {
    material: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    showMetadata: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isEditMode: false,
      editableMaterial: {},
    };
  },
  computed: {
    ...mapState(['loggedInUser']),
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  methods: {
    updateMdListId(newMdListId) {
      const updatedMaterial = { ...this.material, metadata_list_id: newMdListId.id };
      this.$emit("updateMaterial", updatedMaterial);
    },
    clearMdListId() {
      const updatedMaterial = { ...this.material, metadata_list_id: null };
      this.$emit("updateMaterial", updatedMaterial);
    },
    toggleEditMode() {
      this.isEditMode = true;
      this.editableMaterial = JSON.parse(JSON.stringify(this.material));
    },
    cancelEditMode() {
      this.isEditMode = false;
    },
    async saveChanges() {
      try {
        this.$emit('updateMaterial', { ...this.editableMaterial });
        this.isEditMode = false;
      } catch (error) {
        this.toast.error("Error saving changes!");
        console.error('Error saving material changes:', error);
      }
    },
    async deleteMaterial() {
      try {
        const isConfirmed = await confirmDelete("Are you sure you want to delete this Material?");
        if (!isConfirmed) return;

        await apiService.deleteResource(
            API_ENDPOINTS.material(this.material.id),
            this.loggedInUser.id,
            this.loggedInUser.entra_id
        );
        this.$router.push({ name: "MaterialList" });
        this.toast.success("Material deleted successfully!");
      } catch (error) {
        this.toast.error("Failed to delete material!");
        console.error('Error deleting material:', error);
      }
    },
  },
  watch: {
    material: {
      handler(newVal) {
        // If prop updates while editing, keep edit buffer in sync only when not in edit mode
        if (!this.isEditMode && newVal) {
          this.editableMaterial = JSON.parse(JSON.stringify(newVal));
        }
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";

.material-details {
  max-width: 800px;
  margin: 0 auto;
  padding: 15px 20px;
  border-radius: 8px;
  background-color: $secondary-color;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  .header {
    display: flex;
    flex-direction: column;
    gap: 5px;

    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      width: 100%;

      .material-title {
        flex: 1;
        min-width: 0;
        margin: 0;
        overflow-wrap: anywhere;
        word-break: break-word;
      }
    }

    .material-path {
      font-size: 1rem;
      overflow-wrap: anywhere;
      word-break: break-word;

      .material-path-value {
        overflow-wrap: anywhere;
        word-break: break-word;
      }
    }

    .top-buttons {
      flex-shrink: 0;
      display: flex;
      gap: 10px;
    }
  }

  .detail {
    margin-bottom: 3px;
    overflow-wrap: anywhere;
    word-break: break-word;

    .detail-value {
      overflow-wrap: anywhere;
      word-break: break-word;
    }
  }

  .buttons-container {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
}
</style>
